/**
 * Uma aula.
 *
 * **O caso que define esta tela é a aula SEM vídeo.** Hoje ela é a regra, não a exceção: o
 * dono decidiu em 18/09 que a estrutura entra primeiro e os vídeos sobem depois. Então,
 * quando `videoUrl` é nulo, esta tela:
 *
 *  - mostra a aula (não some com ela, não devolve erro, não redireciona);
 *  - diz em português que o vídeo ainda não subiu, no lugar exato onde o player estaria;
 *  - mantém o botão de concluir, porque o texto da aula já é conteúdo — é isso que faz o
 *    "3 de 12" chegar a 12 sem depender de gravação nenhuma.
 *
 * Nada de `<video src={null}>`, nada de player cinza que não toca: um player quebrado
 * afirma "tem vídeo aqui e ele falhou", que é uma mentira diferente do silêncio.
 *
 * **Concluir é clique de gente.** O `onTimeUpdate` do player só guarda ONDE a pessoa
 * parou (`savePosition`) — nunca conclui, nem quando chega ao último segundo. As duas
 * coisas são colunas diferentes no banco de propósito, e deduzir uma da outra aqui jogaria
 * a separação fora na última camada.
 */
import { useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Skeleton,
  SkeletonRegion,
  SkeletonText,
  useToast,
} from '@mgdlead/ui';
import { ArrowLeft, ArrowRight, Check, VideoOff } from 'lucide-react';

import {
  CONCLUIDA_BADGE,
  JA_CONCLUIDA_TOAST,
  SEM_VIDEO,
  completeLabel,
  completeToast,
  formatDuration,
  lessonPath,
} from '../../../features/training/training-ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useCompleteLesson, useSaveLessonPosition, useTrainingLesson } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

/**
 * De quanto em quanto tempo a posição do player vai para o servidor.
 *
 * `timeupdate` dispara ~4x por segundo. Sem esta trava seriam 4 mutações por segundo de
 * vídeo — e o que se está guardando é "onde parei", que não precisa de precisão melhor
 * que isso.
 */
const INTERVALO_POSICAO_MS = 15_000;

export function LessonPage() {
  const { workspace } = useWorkspace();
  const { courseSlug = '', lessonSlug = '' } = useParams<{
    courseSlug: string;
    lessonSlug: string;
  }>();
  const { toast } = useToast();

  const aulaQuery = useTrainingLesson(courseSlug, lessonSlug);
  const concluir = useCompleteLesson();
  const salvarPosicao = useSaveLessonPosition();

  const aula = aulaQuery.data;
  useDocumentTitle(aula?.lesson.title ?? 'Aula');

  const ultimoEnvio = useRef(0);

  /** Guarda onde a pessoa parou. NÃO conclui — ver o cabeçalho deste arquivo. */
  const guardarPosicao = useCallback(
    (lessonId: string, segundos: number) => {
      const agora = Date.now();
      if (agora - ultimoEnvio.current < INTERVALO_POSICAO_MS) return;
      ultimoEnvio.current = agora;
      salvarPosicao.mutate({ lessonId, seconds: Math.floor(segundos) });
    },
    [salvarPosicao],
  );

  const voltarParaCurso = (
    <Link
      to={`/w/${workspace.slug}/treinamentos/${courseSlug}`}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Voltar ao curso
    </Link>
  );

  if (aulaQuery.isError) {
    return (
      <>
        <div className="mb-3">{voltarParaCurso}</div>
        <Card padded={false}>
          <ErrorState
            title="Não foi possível abrir a aula"
            description={humanizeError(aulaQuery.error)}
            onRetry={() => void aulaQuery.refetch()}
          />
        </Card>
      </>
    );
  }

  if (aula === undefined) {
    return (
      <SkeletonRegion label="Carregando a aula" className="flex flex-col gap-3">
        <Skeleton className="h-5 w-3/5" />
        <Skeleton className="aspect-video w-full" />
        <SkeletonText lines={3} />
      </SkeletonRegion>
    );
  }

  const { lesson, position } = aula;
  const concluida = lesson.completedAt !== null;
  const duracao = formatDuration(lesson.durationSeconds);

  const marcar = () => {
    concluir.mutate(
      { lessonId: lesson.id },
      {
        onSuccess: (resultado) =>
          toast({
            title: resultado.alreadyCompleted ? JA_CONCLUIDA_TOAST : completeToast(lesson.hasVideo),
            tone: 'success',
          }),
        onError: (erro: unknown) =>
          toast({
            title: 'Não deu para marcar a aula',
            description: humanizeError(erro),
            tone: 'danger',
          }),
      },
    );
  };

  return (
    <>
      <div className="mb-3">{voltarParaCurso}</div>

      <header className="mb-4 flex flex-col gap-2">
        <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
          {aula.course.title} · {aula.module.title}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold tracking-tight">{lesson.title}</h1>
          {concluida ? (
            <Badge tone="success" icon={<Check className="size-3" />}>
              {CONCLUIDA_BADGE}
            </Badge>
          ) : null}
        </div>
        <p className="text-xs tabular-nums text-muted-foreground">
          Aula {position.index} de {position.total}
          {duracao ? ` · ${duracao}` : ''}
        </p>
      </header>

      {lesson.videoUrl !== null ? (
        <video
          key={lesson.id}
          controls
          preload="metadata"
          className="mb-4 aspect-video w-full rounded-lg border border-border bg-black"
          src={lesson.videoUrl}
          onLoadedMetadata={(event) => {
            // Retoma de onde parou — mas só isso: retomar não é ter concluído.
            if (lesson.lastPositionSeconds > 0) {
              event.currentTarget.currentTime = lesson.lastPositionSeconds;
            }
          }}
          onTimeUpdate={(event) => guardarPosicao(lesson.id, event.currentTarget.currentTime)}
          onPause={(event) => {
            ultimoEnvio.current = 0;
            guardarPosicao(lesson.id, event.currentTarget.currentTime);
          }}
        >
          Seu navegador não consegue reproduzir este vídeo.
        </video>
      ) : (
        /*
          O lugar do player, ocupado por uma FRASE — não por um player quebrado.
          Mesma proporção do vídeo para a tela não pular quando a gravação entrar.
        */
        <Card className="mb-4 flex flex-col items-center justify-center gap-2 py-10 text-center">
          <VideoOff
            aria-hidden="true"
            className="size-8 shrink-0 stroke-[1.5] text-muted-foreground"
          />
          <p className="text-md font-medium">{SEM_VIDEO.title}</p>
          <p className="max-w-prose text-sm text-muted-foreground">{SEM_VIDEO.description}</p>
        </Card>
      )}

      {lesson.description ? (
        <Card className="mb-4">
          <p className="max-w-prose whitespace-pre-line text-sm leading-relaxed text-foreground">
            {lesson.description}
          </p>
        </Card>
      ) : null}

      {/*
        A ação da tela, em largura cheia no celular (DESIGN_SYSTEM seção 11). Já concluída,
        o botão some e sobra o selo no cabeçalho: oferecer "concluir" de novo seria um
        tapinha automático numa aula que já está marcada.
      */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="max-md:w-full">
          {concluida ? (
            <p className="text-sm text-muted-foreground">
              Você já concluiu esta aula. Ela continua contando no seu progresso.
            </p>
          ) : (
            <Button
              variant="primary"
              size="lg"
              className="max-md:w-full"
              loading={concluir.isPending}
              loadingLabel="Marcando a aula"
              leadingIcon={<Check className="size-4" />}
              onClick={marcar}
            >
              {completeLabel(lesson.hasVideo)}
            </Button>
          )}
        </div>

        {aula.next ? (
          <Link
            to={lessonPath(workspace.slug, aula.course.slug, aula.next.slug)}
            className="inline-flex items-center justify-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground max-md:w-full"
          >
            Próxima aula: {aula.next.title}
            <ArrowRight aria-hidden="true" className="size-4" />
          </Link>
        ) : null}
      </div>
    </>
  );
}
